/**
 * Serviço para exportar relatórios em PDF.
 * Envia IDs ao backend e faz download do PDF gerado.
 */
import { api } from './api';
import { toast } from 'react-toastify';
import type { FiltersClientes, FiltersContratos, FiltersFaturas } from '../pages/relatorios/relatorioTypes';

/** Filtros exibidos no PDF do relatório NFCom (espelha NotasNfcomPage). */
export interface FiltersNfcomRelatorio {
  dataInicio?: string;
  dataFim?: string;
  status?: string;
  escritorio?: string;
}

export const relatorioPdfService = {
  /**
   * Exporta relatório de clientes em PDF (A4 horizontal).
   * @param ids - IDs dos clientes do resultado filtrado
   * @param filters - Filtros aplicados (opcional) para exibir no PDF
   */
  async exportarClientes(ids: number[], filters?: FiltersClientes): Promise<void> {
    if (!ids.length) {
      toast.warning('Nenhum registro para exportar.');
      return;
    }
    try {
      const data = (await api.post('/relatorios/pdf/clientes', { ids, filters: filters || {} }, { responseType: 'blob' })) as Blob;
      const blob = data instanceof Blob ? data : new Blob([data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'relatorio_clientes.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF exportado com sucesso.');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Erro ao exportar PDF.');
      throw err;
    }
  },

  /**
   * Exporta relatório de contratos em PDF (A4 horizontal).
   * @param ids - IDs dos contratos do resultado filtrado
   * @param filters - Filtros aplicados (opcional) para exibir no PDF
   */
  async exportarContratos(ids: number[], filters?: FiltersContratos): Promise<void> {
    if (!ids.length) {
      toast.warning('Nenhum registro para exportar.');
      return;
    }
    try {
      const data = (await api.post('/relatorios/pdf/contratos', { ids, filters: filters || {} }, { responseType: 'blob' })) as Blob;
      const blob = data instanceof Blob ? data : new Blob([data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'relatorio_contratos.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF exportado com sucesso.');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Erro ao exportar PDF.');
      throw err;
    }
  },

  /**
   * Exporta relatório financeiro (faturas) em PDF (A4 horizontal).
   * @param ids - IDs das faturas do resultado filtrado
   * @param filters - Filtros aplicados (opcional) para exibir no PDF
   */
  async exportarFaturas(ids: number[], filters?: FiltersFaturas): Promise<void> {
    if (!ids.length) {
      toast.warning('Nenhum registro para exportar.');
      return;
    }
    try {
      const data = (await api.post('/relatorios/pdf/faturas', { ids, filters: filters || {} }, { responseType: 'blob' })) as Blob;
      const blob = data instanceof Blob ? data : new Blob([data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'relatorio_faturas.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF exportado com sucesso.');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Erro ao exportar PDF.');
      throw err;
    }
  },

  /**
   * Exporta relatório de notas NFCom em PDF (A4 horizontal), como nos relatórios de contratos/faturas.
   * @param ids - IDs das notas listadas (após filtros de período, status da API e escritório na tela)
   * @param filters - Período, aba de status e escritório para o cabeçalho do PDF
   */
  async exportarNfcom(ids: number[], filters?: FiltersNfcomRelatorio): Promise<void> {
    if (!ids.length) {
      toast.warning('Nenhum registro para exportar.');
      return;
    }
    try {
      const data = (await api.post('/relatorios/pdf/nfcom', { ids, filters: filters || {} }, { responseType: 'blob' })) as Blob;
      const blob = data instanceof Blob ? data : new Blob([data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'relatorio_notas_nfcom.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF exportado com sucesso.');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Erro ao exportar PDF.');
      throw err;
    }
  },

  /**
   * Exporta extrato financeiro de um cliente ou contrato em PDF (A4 vertical).
   * @param params - Parâmetros de filtro e exibição
   */
  async exportarExtratoFinanceiro(params: {
    cliente_id: number;
    contrato_id?: number;
    somente_pagas?: boolean;
    exibir_valor_fatura?: boolean;
    exibir_valor_pago?: boolean;
    data_inicio?: string;
    data_fim?: string;
  }): Promise<void> {
    try {
      const data = (await api.post('/relatorios/pdf/extrato-financeiro', params, { responseType: 'blob' })) as Blob;
      const blob = data instanceof Blob ? data : new Blob([data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `extrato_financeiro_${params.cliente_id}${params.contrato_id ? `_contrato_${params.contrato_id}` : ''}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Extrato financeiro exportado com sucesso.');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Erro ao exportar extrato financeiro.');
      throw err;
    }
  },
};
